import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import {
  Users, Search, Upload, BarChart3, Database, Plus, MapPin, Phone, Mail,
  FileSpreadsheet, CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp,
  RefreshCw, ArrowUpDown, X, Building2, IdCard
} from "lucide-react";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const UFs = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

/* ─── Tab: CARDMV Leads ─── */
function LeadsTab() {
  const [search, setSearch] = useState("");
  const [uf, setUf] = useState("all");
  const [segmento, setSegmento] = useState("");
  const [tipo, setTipo] = useState("");
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const limit = 50;

  const dSearch = useDebounce(search, 400);
  const dSegmento = useDebounce(segmento, 400);
  const dTipo = useDebounce(tipo, 400);

  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (dSearch) params.set("search", dSearch);
  if (uf && uf !== "all") params.set("uf", uf);
  if (dSegmento) params.set("segmento", dSegmento);
  if (dTipo) params.set("tipo", dTipo);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["/api/cardmv", dSearch, uf, dSegmento, dTipo, offset],
    queryFn: () => authFetch(`/api/cardmv?${params}`),
  });

  const rows: any[] = data?.data ?? [];
  const total: number = data?.total ?? 0;

  const reset = () => { setSearch(""); setUf("all"); setSegmento(""); setTipo(""); setOffset(0); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-48">
          <Label className="text-xs mb-1 block">Busca (nome, CPF, cidade)</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar..."
              value={search}
              onChange={e => { setSearch(e.target.value); setOffset(0); }}
              className="pl-9"
              data-testid="input-leads-search"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs mb-1 block">UF Fazenda</Label>
          <Select value={uf} onValueChange={v => { setUf(v); setOffset(0); }}>
            <SelectTrigger className="w-28" data-testid="select-leads-uf">
              <SelectValue placeholder="UF" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {UFs.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs mb-1 block">Segmento</Label>
          <Input
            placeholder="Ex: Pecuarista"
            value={segmento}
            onChange={e => { setSegmento(e.target.value); setOffset(0); }}
            className="w-36"
            data-testid="input-leads-segmento"
          />
        </div>
        <div>
          <Label className="text-xs mb-1 block">Tipo</Label>
          <Input
            placeholder="Tipo"
            value={tipo}
            onChange={e => { setTipo(e.target.value); setOffset(0); }}
            className="w-28"
            data-testid="input-leads-tipo"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={reset} data-testid="button-leads-reset">
          <X className="h-4 w-4 mr-1" /> Limpar
        </Button>
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
        <span data-testid="text-leads-total">
          {total.toLocaleString("pt-BR")} registro(s)
        </span>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>UF Fazenda</TableHead>
              <TableHead>Cidade Fazenda</TableHead>
              <TableHead>Segmento</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Nenhum resultado encontrado
                </TableCell>
              </TableRow>
            ) : rows.map((r: any) => (
              <>
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  data-testid={`row-lead-${r.id}`}
                >
                  <TableCell className="font-medium">{r.nome ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.cpf}</TableCell>
                  <TableCell>{r.uf_fazenda ?? "—"}</TableCell>
                  <TableCell>{r.cidade_fazenda ?? "—"}</TableCell>
                  <TableCell>{r.segmento1 ? <Badge variant="secondary">{r.segmento1}</Badge> : "—"}</TableCell>
                  <TableCell>{r.tipo ?? "—"}</TableCell>
                  <TableCell>
                    {expanded === r.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </TableCell>
                </TableRow>
                {expanded === r.id && (
                  <TableRow key={`exp-${r.id}`}>
                    <TableCell colSpan={7} className="bg-muted/30 px-6 py-4">
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="font-semibold mb-1 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> Telefones</p>
                          {r.telefones?.length > 0
                            ? r.telefones.map((t: string, i: number) => <div key={i} className="text-muted-foreground">{t}</div>)
                            : <span className="text-muted-foreground">—</span>}
                        </div>
                        <div>
                          <p className="font-semibold mb-1 flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</p>
                          <span className="text-muted-foreground">{r.email ?? "—"}</span>
                        </div>
                        <div>
                          <p className="font-semibold mb-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Fazenda</p>
                          <span className="text-muted-foreground">
                            {[r.endereco_fazenda, r.cidade_fazenda, r.uf_fazenda].filter(Boolean).join(", ") || "—"}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <Button
          variant="outline" size="sm"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
          data-testid="button-leads-prev"
        >Anterior</Button>
        <span className="text-sm text-muted-foreground">
          {offset + 1}–{Math.min(offset + limit, total)} de {total.toLocaleString("pt-BR")}
        </span>
        <Button
          variant="outline" size="sm"
          disabled={offset + limit >= total}
          onClick={() => setOffset(offset + limit)}
          data-testid="button-leads-next"
        >Próximo</Button>
      </div>
    </div>
  );
}

/* ─── Tab: Busca por CPF ─── */
function CpfTab() {
  const [cpfInput, setCpfInput] = useState("");
  const [queryCpf, setQueryCpf] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["/api/cardmv/cpf", queryCpf],
    queryFn: () => authFetch(`/api/cardmv/${queryCpf}`),
    enabled: !!queryCpf,
    retry: false,
  });

  const handleSearch = () => {
    const clean = cpfInput.replace(/\D/g, "").padStart(11, "0");
    if (clean.length === 11 && clean !== "00000000000") {
      setQueryCpf(clean);
    }
  };

  const formatCurrency = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end max-w-md">
        <div className="flex-1">
          <Label className="text-xs mb-1 block">CPF do Proprietário</Label>
          <Input
            placeholder="000.000.000-00"
            value={cpfInput}
            onChange={e => setCpfInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            data-testid="input-cpf-lookup"
          />
        </div>
        <Button onClick={handleSearch} disabled={isLoading} data-testid="button-cpf-search">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Buscar
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>CPF não encontrado no CARDMV.</AlertDescription>
        </Alert>
      )}

      {data && (
        <div className="space-y-4">
          {/* Resumo */}
          <div className="flex flex-wrap gap-2">
            {data.resumo?.tem_lead && <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Lead CARDMV</Badge>}
            {data.resumo?.tem_cadastro_agro && <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Cadastro Hub</Badge>}
            {data.resumo?.tem_divida_pgfn && <Badge variant="destructive">PGFN</Badge>}
            {data.resumo?.tem_autuacao_ibama && <Badge variant="destructive">IBAMA Autuação</Badge>}
            {data.resumo?.tem_embargo_ibama && <Badge variant="destructive">IBAMA Embargo</Badge>}
            {data.resumo?.imoveis_sicar_na_regiao > 0 && (
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                {data.resumo.imoveis_sicar_na_regiao} imóvel(is) SICAR
              </Badge>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Lead Info */}
            {data.lead && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-purple-400" /> Lead CARDMV
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <div><span className="text-muted-foreground">Nome:</span> <strong>{data.lead.nome ?? "—"}</strong></div>
                  <div><span className="text-muted-foreground">CPF:</span> <span className="font-mono">{data.cpf}</span></div>
                  <div><span className="text-muted-foreground">Tipo:</span> {data.lead.tipo ?? "—"}</div>
                  <div><span className="text-muted-foreground">Segmento:</span> {data.lead.segmento1 ?? "—"}</div>
                  <div><span className="text-muted-foreground">Email:</span> {data.lead.email ?? "—"}</div>
                  {data.lead.telefones?.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Telefones:</span>{" "}
                      {data.lead.telefones.join(" | ")}
                    </div>
                  )}
                  {data.lead.fazenda?.cidade && (
                    <div>
                      <span className="text-muted-foreground">Fazenda:</span>{" "}
                      {[data.lead.fazenda.cidade, data.lead.fazenda.uf].filter(Boolean).join(" — ")}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* PGFN */}
            {data.passivos?.pgfn?.length > 0 && (
              <Card className="border-red-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-red-400">
                    <AlertCircle className="h-4 w-4" /> Dívida PGFN
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  {data.passivos.pgfn.map((d: any, i: number) => (
                    <div key={i} className="border-b pb-2 last:border-0">
                      <div className="font-medium">{d.nome_devedor}</div>
                      <div className="text-red-400 font-semibold">{formatCurrency(parseFloat(d.valor_consolidado) || 0)}</div>
                      <div className="text-muted-foreground text-xs">{d.situacao} — {d.tipo_devedor}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* IBAMA Autuações */}
            {data.passivos?.ibama_autuacoes?.length > 0 && (
              <Card className="border-orange-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-orange-400">
                    <AlertCircle className="h-4 w-4" /> IBAMA Autuações ({data.passivos.ibama_autuacoes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  {data.passivos.ibama_autuacoes.slice(0, 5).map((a: any, i: number) => (
                    <div key={i} className="border-b pb-2 last:border-0">
                      <div className="text-muted-foreground text-xs">{a.municipio} — {a.uf}</div>
                      <div>{a.tipo_infracao}</div>
                      <div className="text-orange-400">{formatCurrency(parseFloat(a.valor_multa) || 0)}</div>
                    </div>
                  ))}
                  {data.passivos.total_multas_ibama > 0 && (
                    <div className="font-semibold text-orange-400">
                      Total multas: {formatCurrency(data.passivos.total_multas_ibama)}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SICAR na região */}
            {data.sicar_na_municipio?.total_imoveis > 0 && (
              <Card className="border-blue-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-blue-400">
                    <MapPin className="h-4 w-4" /> SICAR — {data.sicar_na_municipio.municipio}/{data.sicar_na_municipio.uf}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {data.sicar_na_municipio.total_imoveis} imóvel(is) • {Number(data.sicar_na_municipio.area_total_ha).toLocaleString("pt-BR")} ha
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-sm">
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {data.sicar_na_municipio.imoveis.slice(0, 10).map((im: any, i: number) => (
                      <div key={i} className="flex justify-between text-xs border-b pb-1">
                        <span className="font-mono text-muted-foreground">{im.cod_imovel?.slice(0, 18)}</span>
                        <span>{Number(im.num_area).toLocaleString("pt-BR")} ha</span>
                        <Badge variant="outline" className="text-xs h-5">{im.ind_status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Tab: Hub Entities ─── */
interface HubFilterState {
  search: string; entityType: string; state: string; city: string;
  cpfCnpj: string; sourceFile: string;
}
const defaultHubFilters: HubFilterState = {
  search: "", entityType: "all", state: "all", city: "", cpfCnpj: "", sourceFile: "all",
};

function HubTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [view, setView] = useState<"consultar" | "adicionar">("consultar");
  const [filters, setFilters] = useState<HubFilterState>(defaultHubFilters);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const limit = 20;
  const dFilters = useDebounce(filters, 400);

  const updateFilter = (key: keyof HubFilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit), sortField, sortOrder });
  if (dFilters.search) params.set("search", dFilters.search);
  if (dFilters.entityType !== "all") params.set("entityType", dFilters.entityType);
  if (dFilters.state !== "all") params.set("state", dFilters.state);
  if (dFilters.city) params.set("city", dFilters.city);
  if (dFilters.cpfCnpj) params.set("cpfCnpj", dFilters.cpfCnpj);
  if (dFilters.sourceFile !== "all") params.set("sourceFile", dFilters.sourceFile);

  const { data: listData, isLoading } = useQuery({
    queryKey: ["/api/entities", dFilters, page, sortField, sortOrder],
    queryFn: async () => {
      const res = await fetch(`/api/entities?${params}`);
      return res.json();
    },
    enabled: view === "consultar",
  });

  const { data: types } = useQuery({
    queryKey: ["/api/entities/types"],
    queryFn: async () => { const r = await fetch("/api/entities/types"); return r.json(); },
  });

  const { data: sourceFiles } = useQuery({
    queryKey: ["/api/entities/source-files"],
    queryFn: async () => { const r = await fetch("/api/entities/source-files"); return r.json(); },
  });

  const entities = listData?.entities ?? [];
  const total = listData?.total ?? 0;

  /* ─── Add form ─── */
  const [formData, setFormData] = useState({
    entityType: "", name: "", cpfCnpj: "", email: "", phone: "",
    cellphone: "", address: "", city: "", state: "", cep: "",
    region: "", category: "", segment: "", additionalNotes: "",
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const payload: any = { entityType: formData.entityType, name: formData.name, additionalData: {} };
      if (formData.cpfCnpj) payload.cpfCnpj = formData.cpfCnpj;
      if (formData.email) payload.email = formData.email;
      if (formData.phone) payload.phone = formData.phone;
      if (formData.cellphone) payload.cellphone = formData.cellphone;
      if (formData.address) payload.address = formData.address;
      if (formData.city) payload.city = formData.city;
      if (formData.state) payload.state = formData.state;
      if (formData.cep) payload.cep = formData.cep;
      if (formData.region) payload.region = formData.region;
      if (formData.category) payload.category = formData.category;
      if (formData.segment) payload.segment = formData.segment;
      if (formData.additionalNotes) payload.additionalData.notes = formData.additionalNotes;
      const res = await apiRequest("POST", "/api/entities", payload);
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Sucesso!", description: `Entidade #${data.id} criada com sucesso` });
      qc.invalidateQueries({ queryKey: ["/api/entities"] });
      qc.invalidateQueries({ queryKey: ["/api/stats"] });
      setFormData({ entityType: "", name: "", cpfCnpj: "", email: "", phone: "", cellphone: "", address: "", city: "", state: "", cep: "", region: "", category: "", segment: "", additionalNotes: "" });
      setView("consultar");
    },
    onError: (err: Error) => toast({ title: "Erro", description: err.message, variant: "destructive" }),
  });

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortOrder("asc"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={view === "consultar" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("consultar")}
          data-testid="button-hub-consultar"
        ><Search className="h-4 w-4 mr-1" /> Consultar</Button>
        <Button
          variant={view === "adicionar" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("adicionar")}
          data-testid="button-hub-adicionar"
        ><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
      </div>

      {view === "consultar" && (
        <>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-48">
              <Label className="text-xs mb-1 block">Busca geral</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nome, CPF, email..."
                  value={filters.search}
                  onChange={e => updateFilter("search", e.target.value)}
                  className="pl-9"
                  data-testid="input-hub-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Tipo</Label>
              <Select value={filters.entityType} onValueChange={v => updateFilter("entityType", v)}>
                <SelectTrigger className="w-36" data-testid="select-hub-type">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Array.isArray(types) && types.map((t: any) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">UF</Label>
              <Select value={filters.state} onValueChange={v => updateFilter("state", v)}>
                <SelectTrigger className="w-24" data-testid="select-hub-state">
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {UFs.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">CPF/CNPJ</Label>
              <Input
                placeholder="CPF ou CNPJ"
                value={filters.cpfCnpj}
                onChange={e => updateFilter("cpfCnpj", e.target.value)}
                className="w-36"
                data-testid="input-hub-cpf"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setFilters(defaultHubFilters); setPage(0); }}>
              <X className="h-4 w-4 mr-1" /> Limpar
            </Button>
          </div>

          <div className="text-sm text-muted-foreground" data-testid="text-hub-total">
            {total.toLocaleString("pt-BR")} entidade(s)
          </div>

          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-1">Nome <ArrowUpDown className="h-3 w-3" /></div>
                  </TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>CPF/CNPJ</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Cidade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                ) : entities.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum resultado</TableCell></TableRow>
                ) : entities.map((e: any) => (
                  <TableRow key={e.id} data-testid={`row-entity-${e.id}`}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell><Badge variant="secondary">{e.entityType}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{e.cpfCnpj ?? "—"}</TableCell>
                    <TableCell>{e.state ?? "—"}</TableCell>
                    <TableCell>{e.city ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="button-hub-prev">Anterior</Button>
            <span className="text-sm text-muted-foreground">Pág. {page + 1}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} data-testid="button-hub-next">Próximo</Button>
          </div>
        </>
      )}

      {view === "adicionar" && (
        <form onSubmit={e => { e.preventDefault(); if (!formData.entityType || !formData.name) { toast({ title: "Preencha Tipo e Nome", variant: "destructive" }); return; } addMutation.mutate(); }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Nova Entidade Hub</CardTitle>
              <CardDescription>Campos marcados com * são obrigatórios</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Entidade *</Label>
                  <Select value={formData.entityType} onValueChange={v => setFormData(f => ({ ...f, entityType: v }))}>
                    <SelectTrigger data-testid="select-add-entity-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cadastro">Cadastro</SelectItem>
                      <SelectItem value="cliente">Cliente</SelectItem>
                      <SelectItem value="parceiro">Parceiro</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} placeholder="Nome da entidade" data-testid="input-add-name" />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2"><Label>CPF/CNPJ</Label><Input value={formData.cpfCnpj} onChange={e => setFormData(f => ({ ...f, cpfCnpj: e.target.value }))} placeholder="000.000.000-00" data-testid="input-add-cpf" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} placeholder="email@..." data-testid="input-add-email" /></div>
                <div className="space-y-2"><Label>Segmento</Label><Input value={formData.segment} onChange={e => setFormData(f => ({ ...f, segment: e.target.value }))} placeholder="Ex: Pecuarista" data-testid="input-add-segment" /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Telefone</Label><Input value={formData.phone} onChange={e => setFormData(f => ({ ...f, phone: e.target.value }))} placeholder="DDD + Número" data-testid="input-add-phone" /></div>
                <div className="space-y-2"><Label>Celular</Label><Input value={formData.cellphone} onChange={e => setFormData(f => ({ ...f, cellphone: e.target.value }))} placeholder="DDD + Número" data-testid="input-add-cellphone" /></div>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2 col-span-2"><Label>Cidade</Label><Input value={formData.city} onChange={e => setFormData(f => ({ ...f, city: e.target.value }))} placeholder="Cidade" data-testid="input-add-city" /></div>
                <div className="space-y-2">
                  <Label>UF</Label>
                  <Select value={formData.state || "none"} onValueChange={v => setFormData(f => ({ ...f, state: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="select-add-state"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">UF</SelectItem>
                      {UFs.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>CEP</Label><Input value={formData.cep} onChange={e => setFormData(f => ({ ...f, cep: e.target.value }))} placeholder="00000-000" data-testid="input-add-cep" /></div>
              </div>
              <div className="space-y-2">
                <Label>Notas Adicionais</Label>
                <Textarea value={formData.additionalNotes} onChange={e => setFormData(f => ({ ...f, additionalNotes: e.target.value }))} rows={3} placeholder="Informações complementares..." data-testid="textarea-add-notes" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={addMutation.isPending} className="flex-1" data-testid="button-add-submit">
                  {addMutation.isPending ? "Salvando..." : "Salvar Entidade"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setView("consultar")} data-testid="button-add-cancel">Cancelar</Button>
              </div>
            </CardContent>
          </Card>
        </form>
      )}
    </div>
  );
}

/* ─── Tab: Upload ─── */
const IGNORE_VALUE = "__ignore__";
const fieldOptions = [
  { value: "name", label: "Nome" }, { value: "cpfCnpj", label: "CPF/CNPJ" },
  { value: "code", label: "Código" }, { value: "email", label: "Email" },
  { value: "phone", label: "Telefone" }, { value: "cellphone", label: "Celular" },
  { value: "address", label: "Endereço" }, { value: "neighborhood", label: "Bairro" },
  { value: "city", label: "Cidade" }, { value: "state", label: "Estado" },
  { value: "cep", label: "CEP" }, { value: "region", label: "Região" },
  { value: "category", label: "Categoria" }, { value: "segment", label: "Segmento" },
  { value: "segment2", label: "Segmento 2" }, { value: "birthDate", label: "Data Nascimento" },
];

function UploadSection() {
  const { toast } = useToast();
  const [hubFile, setHubFile] = useState<File | null>(null);
  const [hubPreview, setHubPreview] = useState<any>(null);
  const [hubMapping, setHubMapping] = useState<Record<string, string>>({});
  const [hubEntityType, setHubEntityType] = useState("");
  const [hubLoading, setHubLoading] = useState(false);
  const [hubResult, setHubResult] = useState<any>(null);

  const [mvFile, setMvFile] = useState<File | null>(null);
  const [mvLoading, setMvLoading] = useState(false);
  const [mvResult, setMvResult] = useState<any>(null);
  const [mvStatus, setMvStatus] = useState<any>(null);
  const mvFileRef = useRef<HTMLInputElement>(null);

  const { data: importStatus, refetch: refetchStatus } = useQuery({
    queryKey: ["/api/cardmv/import/status"],
    queryFn: () => authFetch("/api/cardmv/import/status"),
    refetchInterval: mvStatus?.running ? 2000 : false,
  });

  const handleHubFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setHubFile(f); setHubResult(null); setHubLoading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const res = await fetch("/api/upload/preview", { method: "POST", body: fd });
      if (!res.ok) throw new Error("Erro ao processar arquivo");
      const data = await res.json();
      setHubPreview(data);
      const init: Record<string, string> = {};
      data.columns.forEach((c: string) => { init[c] = IGNORE_VALUE; });
      setHubMapping(init);
      toast({ title: "Preview carregado!", description: `${data.totalRows} linhas` });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally { setHubLoading(false); }
  };

  const handleHubImport = async () => {
    if (!hubFile || !hubEntityType) { toast({ title: "Selecione arquivo e tipo", variant: "destructive" }); return; }
    const clean: Record<string, string> = {};
    for (const [col, field] of Object.entries(hubMapping)) { if (field && field !== IGNORE_VALUE) clean[col] = field; }
    setHubLoading(true);
    const fd = new FormData();
    fd.append("file", hubFile);
    fd.append("mapping", JSON.stringify(clean));
    fd.append("entityType", hubEntityType);
    try {
      const res = await fetch("/api/upload/import", { method: "POST", body: fd });
      if (!res.ok) throw new Error("Erro ao importar");
      const data = await res.json();
      setHubResult(data);
      toast({ title: "Importação concluída!", description: `${data.imported} de ${data.total} registros` });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally { setHubLoading(false); }
  };

  const handleMvImport = async () => {
    if (!mvFile) { toast({ title: "Selecione um arquivo XLSX", variant: "destructive" }); return; }
    setMvLoading(true);
    const fd = new FormData(); fd.append("file", mvFile);
    try {
      const token = localStorage.getItem("mavrion_jwt_token") || "";
      const raw = await fetch("/api/cardmv/import", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!raw.ok) { const err = await raw.json().catch(() => ({})); throw new Error(err.message || "Erro ao importar"); }
      const res = await raw.json();
      setMvResult(res);
      setMvStatus({ running: true });
      refetchStatus();
      toast({ title: "Importação iniciada!", description: `${res.total_linhas} linhas em processamento` });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message ?? "Falha ao iniciar importação", variant: "destructive" });
    } finally { setMvLoading(false); }
  };

  return (
    <div className="space-y-6">
      {/* CARDMV XLSX Import */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-purple-400" /> Importar Leads CARDMV (XLSX)
          </CardTitle>
          <CardDescription>Planilha com colunas: NOME, CPF, ENDEREÇO, CIDADE, UF, TELEFONE, etc.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {importStatus?.running && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Importação em andamento: {importStatus.inserted} inseridos, {importStatus.skipped} ignorados
                ({importStatus.progress_pct}%)
              </AlertDescription>
            </Alert>
          )}
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
            <FileSpreadsheet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <Label htmlFor="mv-file-upload" className="cursor-pointer">
              <span className="text-primary hover:underline font-medium">Selecionar arquivo XLSX</span>
            </Label>
            <input
              id="mv-file-upload"
              type="file"
              accept=".xlsx"
              onChange={e => setMvFile(e.target.files?.[0] ?? null)}
              className="hidden"
              ref={mvFileRef}
              data-testid="input-mv-file"
            />
            {mvFile && <p className="text-sm text-muted-foreground mt-2">{mvFile.name}</p>}
          </div>
          <Button
            onClick={handleMvImport}
            disabled={!mvFile || mvLoading || importStatus?.running}
            data-testid="button-mv-import"
          >
            {mvLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando...</> : <><Upload className="h-4 w-4 mr-2" /> Importar CARDMV</>}
          </Button>
          {mvResult && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Importação iniciada: {mvResult.total_linhas} linhas em processamento
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Hub Entity Import */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-green-400" /> Importar Entidades Hub (XLSX)
          </CardTitle>
          <CardDescription>Mapeie as colunas da planilha para os campos do sistema</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
            <FileSpreadsheet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <Label htmlFor="hub-file-upload" className="cursor-pointer">
              <span className="text-primary hover:underline font-medium">Selecionar arquivo XLSX</span>
              <span className="text-muted-foreground"> ou arraste</span>
            </Label>
            <input id="hub-file-upload" type="file" accept=".xlsx" onChange={handleHubFile} className="hidden" data-testid="input-hub-file" />
            {hubFile && <p className="text-sm text-muted-foreground mt-2">{hubFile.name}</p>}
          </div>

          {hubPreview && (
            <>
              <div className="space-y-2">
                <Label>Tipo de Entidade</Label>
                <Select value={hubEntityType} onValueChange={setHubEntityType}>
                  <SelectTrigger data-testid="select-hub-entity-type"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="propriedade">Propriedade Rural</SelectItem>
                    <SelectItem value="produtor">Produtor</SelectItem>
                    <SelectItem value="produto">Produto</SelectItem>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="fornecedor">Fornecedor</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Mapeamento de colunas ({hubPreview.totalRows} linhas)</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                  {hubPreview.columns.map((col: string) => (
                    <div key={col} className="flex items-center gap-3">
                      <span className="flex-1 text-sm font-medium truncate">{col}</span>
                      <Select value={hubMapping[col] || IGNORE_VALUE} onValueChange={v => setHubMapping(m => ({ ...m, [col]: v }))}>
                        <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={IGNORE_VALUE}>Ignorar</SelectItem>
                          {fieldOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={handleHubImport} disabled={hubLoading || !hubEntityType} data-testid="button-hub-import">
                {hubLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importando...</> : <><Upload className="h-4 w-4 mr-2" /> Importar Hub</>}
              </Button>
            </>
          )}

          {hubResult && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-500/10 p-3 rounded-lg text-center" data-testid="text-hub-imported">
                <div className="text-2xl font-bold text-green-400">{hubResult.imported}</div>
                <div className="text-xs text-green-400/80">Importados</div>
              </div>
              <div className="bg-muted p-3 rounded-lg text-center">
                <div className="text-2xl font-bold">{hubResult.total}</div>
                <div className="text-xs text-muted-foreground">Total</div>
              </div>
              <div className="bg-red-500/10 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-400">{hubResult.errors?.length ?? 0}</div>
                <div className="text-xs text-red-400/80">Erros</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Tab: Estatísticas ─── */
function EstatisticasTab() {
  const { data: cardmvStats, isLoading: loadingCv } = useQuery({
    queryKey: ["/api/cardmv/stats"],
    queryFn: () => authFetch("/api/cardmv/stats"),
  });

  const { data: hubStats, isLoading: loadingHub } = useQuery({
    queryKey: ["/api/stats"],
    queryFn: async () => { const r = await fetch("/api/stats"); return r.json(); },
  });

  const { data: uploads } = useQuery({
    queryKey: ["/api/uploads"],
    queryFn: async () => { const r = await fetch("/api/uploads"); if (!r.ok) return []; return r.json(); },
  });

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Leads CARDMV</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-400" data-testid="text-stats-cardmv-total">
              {loadingCv ? "—" : (cardmvStats?.total ?? 0).toLocaleString("pt-BR")}
            </div>
            <p className="text-xs text-muted-foreground mt-1">proprietários rurais</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Com SICAR na Região</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-400" data-testid="text-stats-cardmv-sicar">
              {loadingCv ? "—" : (cardmvStats?.leads_com_sicar_na_municipio ?? 0).toLocaleString("pt-BR")}
            </div>
            <p className="text-xs text-muted-foreground mt-1">cruzamento com SICAR</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Entidades Hub</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-400" data-testid="text-stats-hub-total">
              {loadingHub ? "—" : (hubStats?.totalEntities ?? 0).toLocaleString("pt-BR")}
            </div>
            <p className="text-xs text-muted-foreground mt-1">registros no Hub</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Estados Cobertos</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="text-stats-hub-states">
              {loadingHub ? "—" : (hubStats?.topStates?.length ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">UFs com registros</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* CARDMV por UF */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">CARDMV — Leads por UF Fazenda</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCv ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(cardmvStats?.por_uf ?? []).map((item: any) => (
                  <div key={item.uf} className="flex items-center gap-2">
                    <span className="w-8 text-xs font-semibold text-center">{item.uf}</span>
                    <div className="flex-1">
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-purple-500 h-1.5 rounded-full"
                          style={{ width: `${Math.max(2, (item.total / (cardmvStats?.total || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-16 text-right">{item.total.toLocaleString("pt-BR")}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CARDMV por Segmento */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">CARDMV — Por Segmento</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingCv ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(cardmvStats?.por_segmento ?? []).map((item: any) => (
                  <div key={item.segmento} className="flex items-center gap-2">
                    <span className="flex-1 text-xs truncate">{item.segmento ?? "—"}</span>
                    <div className="w-24">
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-amber-500 h-1.5 rounded-full"
                          style={{ width: `${Math.max(2, (item.total / (cardmvStats?.total || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-14 text-right">{item.total.toLocaleString("pt-BR")}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hub por Tipo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Hub — Entidades por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHub ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <div className="space-y-2">
                {(hubStats?.byType ?? []).map((item: any) => (
                  <div key={item.entityType} className="flex items-center gap-2">
                    <span className="flex-1 text-xs capitalize">{item.entityType ?? "—"}</span>
                    <div className="w-24">
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className="bg-green-500 h-1.5 rounded-full"
                          style={{ width: `${Math.max(2, (parseInt(item.count) / (hubStats?.totalEntities || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-14 text-right">{parseInt(item.count).toLocaleString("pt-BR")}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Histórico de uploads Hub */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Hub — Histórico de Uploads</CardTitle>
          </CardHeader>
          <CardContent>
            {!Array.isArray(uploads) || uploads.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">Nenhum upload realizado</div>
            ) : (
              <div className="space-y-2">
                {uploads.slice(0, 8).map((u: any) => (
                  <div key={u.id} className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs" data-testid={`upload-hist-${u.id}`}>
                    <div>
                      <div className="font-medium">{u.fileName}</div>
                      <div className="text-muted-foreground">{new Date(u.uploadedAt).toLocaleString("pt-BR")}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-green-400 font-semibold">{u.recordsImported} importados</div>
                      <Badge variant="secondary" className="text-xs">{u.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ─── Page Principal ─── */
export default function Cardmv0Page() {
  const { data: stats } = useQuery({
    queryKey: ["/api/cardmv/stats"],
    queryFn: () => authFetch("/api/cardmv/stats"),
  });
  const { data: hubStats } = useQuery({
    queryKey: ["/api/stats"],
    queryFn: async () => { const r = await fetch("/api/stats"); return r.json(); },
  });

  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["/api/cardmv"] });
    qc.invalidateQueries({ queryKey: ["/api/cardmv/stats"] });
    qc.invalidateQueries({ queryKey: ["/api/stats"] });
  };

  return (
    <div className="space-y-6" data-testid="page-cardmv-0">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <IdCard className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-cardmv0-title">CARDMV-0</h1>
            <p className="text-muted-foreground text-sm">
              Cadastro de Proprietários Rurais — Leads & Hub de Entidades
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-sm">
            <div className="font-semibold text-purple-400" data-testid="text-header-cardmv-total">
              {stats?.total != null ? stats.total.toLocaleString("pt-BR") : "—"} leads
            </div>
            <div className="text-xs text-muted-foreground">
              {hubStats?.totalEntities != null ? hubStats.totalEntities.toLocaleString("pt-BR") : "—"} entidades hub
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="button-cardmv0-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="mb-4" data-testid="tabs-cardmv0">
          <TabsTrigger value="leads" data-testid="tab-leads"><Users className="h-4 w-4 mr-1.5" />Leads</TabsTrigger>
          <TabsTrigger value="cpf" data-testid="tab-cpf"><Search className="h-4 w-4 mr-1.5" />Busca CPF</TabsTrigger>
          <TabsTrigger value="hub" data-testid="tab-hub"><Database className="h-4 w-4 mr-1.5" />Hub</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload"><Upload className="h-4 w-4 mr-1.5" />Upload</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 className="h-4 w-4 mr-1.5" />Estatísticas</TabsTrigger>
        </TabsList>

        <TabsContent value="leads"><LeadsTab /></TabsContent>
        <TabsContent value="cpf"><CpfTab /></TabsContent>
        <TabsContent value="hub"><HubTab /></TabsContent>
        <TabsContent value="upload"><UploadSection /></TabsContent>
        <TabsContent value="stats"><EstatisticasTab /></TabsContent>
      </Tabs>
    </div>
  );
}
